import { Observable, type Observable as ObservableType } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import type { SprintArray } from '../../../model/sprint/sprint-types.tsx';
import type { JQLSwimlaneData } from '../../../model/work/work-types.tsx';
import {
	fetchBoardDeferredData,
	type BoardDeferredDataParams,
	type BoardDeferredDataResponse,
} from '../../../rest/board/board-deferred-data/index.tsx';
import type { State } from '../../../state/reducers/types.tsx';
import {
	getEntities,
	rapidViewIdSelector,
} from '../../../state/selectors/software/software-selectors.tsx';
import {
	getSprintEntities,
	isSprintsEnabled as isSprintsEnabledSelector,
} from '../../../state/selectors/sprint/sprint-selectors.tsx';
import { isSwimlanesByJqlAvailable } from '../../../state/selectors/swimlane/swimlane-mode-selectors.tsx';

export interface BoardDeferredData {
	sprints?: SprintArray;
	jqlSwimlanes?: JQLSwimlaneData[];
	isUsingSimplifiedWorkflow?: boolean;
}

function getParamsFromState(state: State): BoardDeferredDataParams {
	const rapidViewId = rapidViewIdSelector(state);
	const isSprintsEnabled = isSprintsEnabledSelector(state);
	const isJqlSwimlanesEnabled = isSwimlanesByJqlAvailable(state);
	const sprintIds = isSprintsEnabled
		? Object.values(getSprintEntities(state)).map((sprint) => sprint.id)
		: undefined;

	const fields = isJqlSwimlanesEnabled ? ['swimlanes_query'] : [];

	return {
		rapidViewId,
		fields,
		sprintIds,
	};
}

function transformResponse(
	state: State,
	{ sprintsData, swimlanesData, workflowData }: BoardDeferredDataResponse,
): BoardDeferredData {
	// The sprint data returned from deferred endpoint does not include everything, so fill out the sprint type with value from state, overriding updated values (canUpdateSprint).
	const existingSprints = getSprintEntities(state);
	const sprints =
		sprintsData?.sprints.map(({ id, canUpdateSprint }) => ({
			...existingSprints[id],
			canUpdateSprint,
		})) ?? undefined;

	// The swimlane data returned from the deferred endpoint is not complete, so fill out the swimlanes with value from state, while overriding updated values (query).
	const existingSwimlanes = getEntities(state).jqlSwimlanes.swimlanes;
	const jqlSwimlanes = swimlanesData
		? existingSwimlanes.map((swimlane) => ({
				...swimlane,
				query:
					swimlanesData.customSwimlanesData.swimlanes.find((s) => String(s.id) === swimlane.id)
						?.query ?? swimlane.query,
			}))
		: undefined;

	const isUsingSimplifiedWorkflow = workflowData?.isUsingSimplifiedWorkflow;

	return { sprints, jqlSwimlanes, isUsingSimplifiedWorkflow };
}

export function boardDeferredDataService(state: State): ObservableType<BoardDeferredData> {
	const params = getParamsFromState(state);
	return params.fields.length
		? fetchBoardDeferredData(params).map((response) => transformResponse(state, response))
		: Observable.of({});
}
